'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'

import type { GetCachedIncrementalTicker } from '@/service/ws/incremental-ticker-cache'
import { listenerScopesForPayloads } from '@/service/ws/scoped-listener-registry'
import type { GetCachedTicker } from '@/service/ws/ticker-channel-cache'
import useBaseWebSocket from '@/service/ws/use-base-websocket'
import { useWebSocketContext } from '@/service/ws/websocket-context'
import { MarketDataStream, MessageSubject, ServerMessage } from '@/types/generated/public_api'
import { WebSocketRequest } from '@/types/service'

// Re-export for backward compatibility
export default useBaseWebSocket

// ============================================
// Types
// ============================================

type ChannelKey = keyof Omit<MessageSubject, '$type'>

/**
 * A market-data subscription payload. `depth` is an order-book-only cap, and
 * every other stream leaves it out — `ClientMessage.create` fills the proto
 * default, which is the wire's "whole book".
 */
export type MarketDataStreamPayload = Omit<MarketDataStream, 'depth'> & { depth?: number }
type DelayedSubscribeCancel = () => void

/** Channel key → Response channel key mapping for listener routing */
const CHANNEL_RESPONSE_MAP: Record<string, string> = {
  marketDataStream: 'marketDataStream:tickers',
  chartDataStream: 'chartDataStream',
  accountStream: 'accountStream',
  account: 'account', // Legacy alias
  ordersStream: 'ordersStream',
  positionStream: 'positionStream',
  tradeListStream: 'tradeListStream',
  priceFeedStream: 'priceFeedStream',
  exchangeData: 'exchangeData',
  execution: 'execution', // Execution notifications (order confirmations, errors)
  serviceNotifications: 'serviceNotifications', // Order notifications (filled, rejected, validation errors)
  economicCalendarStream: 'economicCalendarStream', // Real-time economic calendar events
  coinmarketcalEventStream: 'coinmarketcalEventStream', // ENG-870: real-time CoinMarketCal event calendar
  dbNewsStream: 'dbNewsStream', // Real-time DB news posts
  alpacaNewsStream: 'alpacaNewsStream', // ENG-838: real-time Alpaca (Benzinga) news posts
  twitterStream: 'twitterStream', // ENG-841: real-time Twitter analyst feed posts
  treeNewsStream: 'treeNewsStream', // ENG-1026: real-time Tree of Alpha news/social feed
  executionAnalyticsStream: 'executionAnalyticsStream',
  analyticsStream: 'analyticsStream',
  triggerConditionStream: 'triggerConditionStream', // Live per-trigger evaluation snapshots
  optionsVolumeStream: 'optionsVolumeStream', // ENG-613: live options volume buckets
  optionsTradesStream: 'optionsTradesStream', // ENG-613: server-filtered options trade tape
  exchangeStatusStream: 'exchangeStatusStream', // Live venue open/closed + maintenance/outage push
  venueHealthStream: 'venueHealthStream', // Live per-venue data and trading health
  marketScreenerStream: 'marketScreenerStream', // Market screener snapshot + 1s deltas, per venue-filter token
}

export function scheduleDelayedSubscribe(
  channels: Partial<MessageSubject>[],
  subscribe: (channels: Partial<MessageSubject>[]) => void,
  delayMs: number,
  isActive: () => boolean = () => true,
  onOpen?: () => void
): DelayedSubscribeCancel {
  const timeoutId = setTimeout(() => {
    if (!isActive()) return
    subscribe(channels)
    onOpen?.()
  }, delayMs)

  return () => clearTimeout(timeoutId)
}

/**
 * Builds a response from the provider's cache for newly subscribed payloads. Joining a channel
 * that is already live (shared with another widget, or lingering after a page switch) gets no
 * server snapshot, so without this the widget waits for the next update.
 */
type Replay<TPayload, TResponse> = (
  getCachedTicker: GetCachedTicker,
  payloads: TPayload[],
  getCachedIncrementalTicker: GetCachedIncrementalTicker
) => TResponse | undefined

function deliverReplay<TPayload, TResponse>(
  replay: Replay<TPayload, TResponse> | undefined,
  getCachedTicker: GetCachedTicker,
  payloads: TPayload[],
  onMessage: ((response: TResponse) => void) | undefined,
  getCachedIncrementalTicker: GetCachedIncrementalTicker
) {
  if (!replay || payloads.length === 0) return
  const response = replay(getCachedTicker, payloads, getCachedIncrementalTicker)
  if (response) onMessage?.(response)
}

interface StreamConfig<TPayload, TResponse> {
  /** Key in MessageSubject for subscribe channel */
  channelKey: ChannelKey
  /** Function to extract response data from ServerMessage */
  extractResponse: (message: ServerMessage, payload: TPayload) => TResponse | undefined
  /** Function to get payload dependencies for memo */
  getPayloadDeps?: (payload: TPayload) => unknown[]
  /** Channel key for listener routing (auto-derived if not provided) */
  listenerChannelKey?: string
  replay?: Replay<TPayload, TResponse>
  /** @deprecated Hook name for logging (kept for compatibility) */
  hookName?: string
}

interface MultiStreamConfig<TPayload, TResponse> {
  channelKey: ChannelKey
  extractResponse: (message: ServerMessage, payloads: TPayload[]) => TResponse | undefined
  listenerChannelKey?: string
  replay?: Replay<TPayload, TResponse>
  /** @deprecated Hook name for logging (kept for compatibility) */
  hookName?: string
}

// ============================================
// Single Stream Hook Factory
// ============================================

export function createStreamHook<TPayload, TResponse>(config: StreamConfig<TPayload, TResponse>) {
  const { channelKey, extractResponse, getPayloadDeps, replay } = config
  const listenerChannelKey = config.listenerChannelKey || CHANNEL_RESPONSE_MAP[channelKey]

  return function useStreamHook({
    connected,
    payload,
    onMessage,
    onOpen,
    onClose,
  }: WebSocketRequest<TPayload, TResponse>) {
    const {
      subscribe,
      unsubscribe,
      refreshSubscriptions,
      getCachedTicker,
      getCachedIncrementalTicker,
      addChannelListener,
      removeChannelListener,
      connectionStatus,
    } = useWebSocketContext()

    const payloadKey = JSON.stringify(getPayloadDeps ? getPayloadDeps(payload) : [payload])
    const listenerScopeKey =
      listenerScopesForPayloads(listenerChannelKey, [payload])?.join('|') ?? ''
    const listenerScopes = useMemo(
      () => (listenerScopeKey ? listenerScopeKey.split('|') : undefined),
      [listenerScopeKey]
    )

    const onMessageRef = useRef(onMessage)
    const payloadRef = useRef(payload)
    const onOpenRef = useRef(onOpen)
    const onCloseRef = useRef(onClose)
    useEffect(() => {
      onMessageRef.current = onMessage
      payloadRef.current = payload
      onOpenRef.current = onOpen
      onCloseRef.current = onClose
    }, [onClose, onMessage, onOpen, payload, payloadKey])

    // Keep provider callbacks current without changing the stream listener's identity.
    const subscribeRef = useRef(subscribe)
    const unsubscribeRef = useRef(unsubscribe)
    useEffect(() => {
      subscribeRef.current = subscribe
      unsubscribeRef.current = unsubscribe
    }, [subscribe, unsubscribe])
    // Track subscription state to avoid duplicates
    const isSubscribedRef = useRef(false)
    const subscribedPayloadKeyRef = useRef<string>('')
    const subscribedChannelRef = useRef<Partial<MessageSubject> | null>(null)

    // Register the listener before subscribing so an immediate snapshot response is not missed.
    useEffect(() => {
      if (!connected) return

      const handleMessage = (message: ServerMessage) => {
        const response = extractResponse(message, payloadRef.current)
        if (response) {
          onMessageRef.current?.(response)
        }
      }

      addChannelListener(listenerChannelKey, handleMessage, listenerScopes)
      return () => removeChannelListener(listenerChannelKey, handleMessage, listenerScopes)
    }, [connected, addChannelListener, listenerScopes, removeChannelListener])

    // Subscription management
    useEffect(() => {
      // Early exit if not connected
      if (!connected) {
        // Cleanup if we were previously subscribed
        if (isSubscribedRef.current && subscribedChannelRef.current) {
          unsubscribeRef.current([subscribedChannelRef.current])
          subscribedChannelRef.current = null
          isSubscribedRef.current = false
          subscribedPayloadKeyRef.current = ''
          onCloseRef.current?.(new CloseEvent('close'))
        }
        return
      }

      const channel = { [channelKey]: payload } as Partial<MessageSubject>
      const payloadChanged = payloadKey !== subscribedPayloadKeyRef.current

      // If already subscribed with same payload, skip
      if (isSubscribedRef.current && !payloadChanged) {
        return
      }

      // If payload changed, unsubscribe from old channel first
      if (isSubscribedRef.current && payloadChanged && subscribedChannelRef.current) {
        unsubscribeRef.current([subscribedChannelRef.current])
      }

      // Store channel for cleanup
      subscribedChannelRef.current = channel
      subscribedPayloadKeyRef.current = payloadKey
      isSubscribedRef.current = true

      subscribeRef.current([channel])
      deliverReplay(
        replay,
        getCachedTicker,
        [payload],
        onMessageRef.current,
        getCachedIncrementalTicker
      )
      onOpenRef.current?.()

      return () => {
        if (subscribedChannelRef.current) {
          unsubscribeRef.current([subscribedChannelRef.current])
          subscribedChannelRef.current = null
          isSubscribedRef.current = false
          subscribedPayloadKeyRef.current = ''
        }
        onCloseRef.current?.(new CloseEvent('close'))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, payloadKey])

    // Force a real wire refresh without changing the provider's shared ref-count.
    // Returns the number of channels actually refreshed on the wire — 0 means the
    // provider's per-channel cooldown swallowed the request. Resync loops use this to
    // avoid counting a refresh that never went out (see useOrderbookResync).
    const flushAndReconnect = useCallback(
      (newPayload?: TPayload): number => {
        const targetPayload = newPayload ?? payloadRef.current
        const channel = { [channelKey]: targetPayload } as Partial<MessageSubject>
        return refreshSubscriptions([channel])
      },
      [refreshSubscriptions]
    )

    return { connectionStatus, flushAndReconnect }
  }
}

// ============================================
// Multi Stream Hook Factory
// ============================================

export function createMultiStreamHook<TPayload, TResponse>(
  config: MultiStreamConfig<TPayload, TResponse>
) {
  const { channelKey, extractResponse, replay } = config
  const listenerChannelKey = config.listenerChannelKey || CHANNEL_RESPONSE_MAP[channelKey]

  return function useMultiStreamHook({
    connected = true,
    payloads,
    onMessage,
    onOpen,
    onClose,
  }: {
    connected?: boolean
    payloads: TPayload[]
    onMessage?: (response: TResponse) => void
    onOpen?: () => void
    onClose?: (event: CloseEvent) => void
  }) {
    const {
      subscribe,
      unsubscribe,
      refreshSubscriptions,
      getCachedTicker,
      getCachedIncrementalTicker,
      addChannelListener,
      removeChannelListener,
      connectionStatus,
    } = useWebSocketContext()

    const payloadsKey = useMemo(() => {
      // Sort payloads to ensure consistent key regardless of order
      const sortedPayloads = [...payloads].sort((a, b) =>
        JSON.stringify(a).localeCompare(JSON.stringify(b))
      )
      return JSON.stringify(sortedPayloads)
    }, [payloads])
    const listenerScopeKey =
      listenerScopesForPayloads(listenerChannelKey, payloads)?.join('|') ?? ''
    const listenerScopes = useMemo(
      () => (listenerScopeKey ? listenerScopeKey.split('|') : undefined),
      [listenerScopeKey]
    )

    const onMessageRef = useRef(onMessage)
    const payloadsRef = useRef(payloads)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
      onMessageRef.current = onMessage
    }, [onMessage])

    useEffect(() => {
      onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
      payloadsRef.current = payloads
    }, [payloads, payloadsKey])

    // Keep provider callbacks current without changing the stream listener's identity.
    const subscribeRef = useRef(subscribe)
    const unsubscribeRef = useRef(unsubscribe)
    useEffect(() => {
      subscribeRef.current = subscribe
      unsubscribeRef.current = unsubscribe
    }, [subscribe, unsubscribe])

    // Flush current subscriptions and reconnect to get fresh snapshot
    const flushAndReconnect = useCallback(
      (newPayloads?: TPayload[]): number => {
        const targetPayloads = newPayloads ?? payloadsRef.current
        const channels = targetPayloads.map((p) => ({ [channelKey]: p }) as Partial<MessageSubject>)
        return refreshSubscriptions(channels)
      },
      [refreshSubscriptions]
    )

    // Keep track of currently subscribed channels for cleanup
    const subscribedChannelsRef = useRef<Partial<MessageSubject>[]>([])
    // Track if we're currently subscribed to avoid duplicate subscriptions
    const isSubscribedRef = useRef(false)
    // Track the payloadsKey that we're currently subscribed to
    const subscribedPayloadsKeyRef = useRef<string>('')

    // Register the listener before subscribing so an immediate snapshot response is not missed.
    useEffect(() => {
      if (!connected) return

      const handleMessage = (message: ServerMessage) => {
        const response = extractResponse(message, payloadsRef.current)
        if (response) {
          onMessageRef.current?.(response)
        }
      }

      addChannelListener(listenerChannelKey, handleMessage, listenerScopes)
      return () => removeChannelListener(listenerChannelKey, handleMessage, listenerScopes)
    }, [connected, addChannelListener, listenerScopes, removeChannelListener])

    // Subscription management
    useEffect(() => {
      const cleanupSubscription = () => {
        // Use the stored channels, not the current payloads
        if (subscribedChannelsRef.current.length > 0) {
          unsubscribeRef.current(subscribedChannelsRef.current)
          subscribedChannelsRef.current = []
          isSubscribedRef.current = false
          subscribedPayloadsKeyRef.current = ''
        }
        // Via the ref, like the unmount cleanup below: this effect only re-runs on
        // connected/payloadsKey, so the closure's onClose is not guaranteed current.
        onCloseRef.current?.(new CloseEvent('close'))
      }

      // Early exit if not connected or no payloads
      if (!connected || payloads.length === 0) {
        // Cleanup if we were previously subscribed
        if (isSubscribedRef.current && subscribedChannelsRef.current.length > 0) {
          cleanupSubscription()
        }
        return
      }

      const channels = payloads.map(
        (payload) => ({ [channelKey]: payload }) as Partial<MessageSubject>
      )

      // Check if payloads actually changed
      const payloadsChanged = payloadsKey !== subscribedPayloadsKeyRef.current

      // If already subscribed with same payloads, skip
      if (isSubscribedRef.current && !payloadsChanged) {
        return
      }

      // If payloads changed, diff against the old channel set instead of unsubscribing
      // everything and resubscribing everything — a virtualized/scrolling watchlist can
      // change payloads every ~150ms with mostly-overlapping symbols, and churning the
      // whole set on every change risks the server rate-limiting a burst of redundant
      // subscribe/unsubscribe messages (which fails silently client-side — see
      // websocket-context.tsx's `message.error` handling).
      if (isSubscribedRef.current && payloadsChanged && subscribedChannelsRef.current.length > 0) {
        const oldChannels = subscribedChannelsRef.current
        const oldKeys = new Set(oldChannels.map((ch) => JSON.stringify(ch[channelKey])))
        const newKeys = new Set(channels.map((ch) => JSON.stringify(ch[channelKey])))

        const toRemove = oldChannels.filter((ch) => !newKeys.has(JSON.stringify(ch[channelKey])))
        const toAdd = channels.filter((ch) => !oldKeys.has(JSON.stringify(ch[channelKey])))

        if (toRemove.length > 0) unsubscribeRef.current(toRemove)

        // Store channels for cleanup
        subscribedChannelsRef.current = channels
        subscribedPayloadsKeyRef.current = payloadsKey
        isSubscribedRef.current = true

        // A disjoint set used to wait 100ms before subscribing "to ensure clean state".
        // The server's receive loop is sequential, so the unsubscribe above is applied
        // before the subscribe below regardless — the wait only delayed the snapshot.
        // Measured on an option-chain expiry switch (prod build): click→subscribe
        // 200–300ms, of which this timer was a flat 100ms.
        if (toAdd.length > 0) {
          subscribeRef.current(toAdd)
          const added = toAdd.map((ch) => ch[channelKey] as TPayload)
          deliverReplay(
            replay,
            getCachedTicker,
            added,
            onMessageRef.current,
            getCachedIncrementalTicker
          )
          onOpen?.()
        }

        return
      }

      // Store channels for cleanup
      subscribedChannelsRef.current = channels
      subscribedPayloadsKeyRef.current = payloadsKey
      isSubscribedRef.current = true

      subscribeRef.current(channels)
      deliverReplay(
        replay,
        getCachedTicker,
        payloads,
        onMessageRef.current,
        getCachedIncrementalTicker
      )
      onOpen?.()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, payloadsKey])

    // Only tear down subscriptions on actual unmount — this effect intentionally does
    // NOT depend on payloadsKey. If it did, React would run this cleanup before every
    // payloadsKey-driven re-run of the effect above, unsubscribing everything and
    // resetting isSubscribedRef before the diff branch got a chance to run, turning
    // every payload change (e.g. scrolling a watchlist) into a full unsubscribe-all +
    // resubscribe-all instead of an incremental diff.
    useEffect(() => {
      return () => {
        if (subscribedChannelsRef.current.length > 0) {
          unsubscribeRef.current(subscribedChannelsRef.current)
          subscribedChannelsRef.current = []
          isSubscribedRef.current = false
          subscribedPayloadsKeyRef.current = ''
          onCloseRef.current?.(new CloseEvent('close'))
        }
      }
    }, [])

    return { connectionStatus, flushAndReconnect }
  }
}
